import React, { useEffect, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_URL + '/api';

export default function DepartmentContractMapper() {
  const [departments, setDepartments] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [pbps, setPbps] = useState({});
  const [selectedDept, setSelectedDept] = useState('');
  const [selection, setSelection] = useState({});
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetch(`${API_BASE}/departments/with-aliases`, { credentials: 'include' })
      .then(res => res.json())
      .then(data => setDepartments(data.filter(d => d.submitted)));

    fetch(`${API_BASE}/contracts`, { credentials: 'include' })
      .then(res => res.json())
      .then(data => setContracts(data.contracts));
  }, []);

  const loadPbps = async (contractId) => {
    if (pbps[contractId]) return;
    const res = await fetch(`${API_BASE}/contracts/${contractId}/pbps`, { credentials: 'include' });
    const data = await res.json();
    if (data.success) {
      setPbps(prev => ({ ...prev, [contractId]: data.pbps }));
    }
  };

  const toggle = (contractId, pbpId) => {
    const key = `${contractId}|${pbpId}`;
    setSelection(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSubmit = async () => {
    if (!selectedDept) return setMessage('❌ Choose a department.');

    const mappings = Object.entries(selection)
      .filter(([, v]) => v)
      .map(([key]) => {
        const [contract_id, pbp_id] = key.split('|');
        return { contract_id, pbp_id };
      });

    if (!mappings.length) return setMessage('❌ No mappings selected.');

    // Real backend route is POST /api/departments/map-contracts. The old
    // /department-contract-mapping URL was never wired up server-side, so
    // every mapping save 404'd silently — that's why this page felt broken.
    const res = await fetch(`${API_BASE}/departments/map-contracts`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ department_id: selectedDept, mappings }),
    });

    const data = await res.json();
    if (data.success) {
      setSelection({});
      setMessage('✅ Mappings saved.');
    } else {
      setMessage(`❌ ${data.message}`);
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-4">📎 Map Department → Contract + PBPs</h2>

      <div className="mb-4 flex gap-3 items-center">
        <label className="text-sm font-medium">Select Department:</label>
        <select
          value={selectedDept}
          onChange={(e) => setSelectedDept(e.target.value)}
          className="border px-3 py-2 rounded"
        >
          <option value="">-- Choose --</option>
          {departments.map((d) => (
            <option key={d.id} value={d.id}>{d.name}</option>
          ))}
        </select>
        <button
          onClick={handleSubmit}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Save Mappings
        </button>
      </div>

      <div className="text-sm text-gray-600 mb-4">{message}</div>

      <div className="space-y-6">
        {contracts.map((c) => (
          <div key={c.id} className="bg-white rounded border shadow p-4">
            <div className="font-semibold text-blue-800">
              {c.name} ({c.plan_id})
              <button
                className="ml-3 text-sm text-blue-500 underline"
                onClick={() => loadPbps(c.id)}
              >
                🔽 Load PBPs
              </button>
            </div>

            {pbps[c.id] && (
              <ul className="text-sm mt-2 ml-4 grid grid-cols-3 gap-2">
                {pbps[c.id].map((pbpId) => {
                  const key = `${c.id}|${pbpId}`;
                  return (
                    <li key={pbpId} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={selection[key] || false}
                        onChange={() => toggle(c.id, pbpId)}
                      />
                      <span>{pbpId}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
